import { useState, useEffect } from 'react';
import { X, Home, MessageSquare, Lightbulb, Archive, Settings, Megaphone, Gift, HelpCircle, LogOut, BarChart2, BookOpen, CreditCard, Share2, ChevronDown, ChevronRight } from 'lucide-react';
import { Link } from 'wouter';
import { UserButton, useClerk } from '@clerk/clerk-react';
import { useCoachingSessionStore } from '../lib/coachingSessionStore';

interface MobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  currentPage?: string;
}

const MobileDrawer = ({ isOpen, onClose, currentPage }: MobileDrawerProps) => {
  const { signOut } = useClerk();
  const sessionId = useCoachingSessionStore((state) => state.sessionId);
  const [isAnimating, setIsAnimating] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      document.body.style.overflow = 'hidden';
    } else {
      const timer = setTimeout(() => setIsAnimating(false), 300);
      document.body.style.overflow = '';
      return () => clearTimeout(timer);
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Toggle submenu expansion
  const toggleSubmenu = (menuId: string) => {
    const newExpanded = new Set(expandedMenus);
    if (newExpanded.has(menuId)) {
      newExpanded.delete(menuId);
    } else {
      newExpanded.add(menuId);
    }
    setExpandedMenus(newExpanded);
  };

  // Handle recommend share action
  const handleRecommendShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'HiPoAiCoach - AI-Powered Personal Growth',
        text: 'I\'ve been using HiPoAiCoach for my professional development. Check out these AI coaches that help unlock effortless high performance!',
        url: window.location.origin
      });
    } else {
      navigator.clipboard.writeText(`Check out HiPoAiCoach - AI-Powered Personal Growth! ${window.location.origin}`);
    }
    onClose();
  };

  // Handle quick tour action
  const handleQuickTourMobile = () => {
    // Trigger tour on HomePage if available
    const tourEvent = new CustomEvent('openTour');
    window.dispatchEvent(tourEvent);
    onClose();
  };

  const navItems = [
    // PRIMARY NAVIGATION
    { id: 'home', icon: Home, label: 'Home', path: '/dashboard' },
    { id: 'coaching', icon: MessageSquare, label: 'Coaching', path: sessionId ? `/coaching/session?session=${sessionId}` : '/coaching' },
    
    // INSIGHTS HUB WITH SUBMENU
    { 
      id: 'insights-hub', 
      icon: Lightbulb, 
      label: 'Insights Hub', 
      path: '/insights-hub',
      submenu: [
        { id: 'insights', icon: BarChart2, label: 'Insights', path: '/insights' },
        { id: 'archive', icon: Archive, label: 'MoM Archive', path: '/archive' },
        { id: 'wishlist', icon: BookOpen, label: 'Action Ledger', path: '/wishlist' },
      ]
    },
    
    // MY ACCOUNT WITH SUBMENU
    { 
      id: 'settings', 
      icon: Settings, 
      label: 'My Account', 
      path: '/settings',
      submenu: [
        { id: 'settings-main', icon: Settings, label: 'Settings', path: '/settings' },
        { id: 'billing', icon: CreditCard, label: 'Subscription & Billing', path: '/payments#subscribe' },
        { id: 'logout', icon: LogOut, label: 'Logout', path: '/settings#signout' },
      ]
    },
    
    // SHARE HIPO WITH SUBMENU  
    { 
      id: 'share', 
      icon: Share2, 
      label: 'Share HiPo', 
      submenu: [
        { id: 'gift', icon: Gift, label: 'Gifting Module', path: '/payments' },
        { id: 'recommend', icon: Megaphone, label: 'Recommend', action: 'share' },
      ]
    },
    
    // QUICK TOUR SECTION
    { 
      id: 'quick-tour', 
      icon: HelpCircle, 
      label: 'Quick Tour', 
      action: 'tour'
    },
    
    // FEEDBACK SECTION
    { 
      id: 'feedback', 
      icon: Megaphone, 
      label: 'Feedback', 
      path: '/feedback'
    },
  ];

  const handleNavClick = (path: string) => {
    onClose();
  };

  const handleSignOut = () => {
    signOut();
    onClose();
  };

  if (!isOpen && !isAnimating) return null;

  return (
    <>
      {/* Backdrops */}
      <div
        className={`fixed inset-0 z-[60] bg-black transition-opacity duration-300 lg:hidden ${isOpen ? 'opacity-50' : 'opacity-0'
          }`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`fixed left-0 top-0 z-[70] h-full w-80 max-w-[85vw] bg-white shadow-2xl transition-transform duration-300 ease-out lg:hidden ${isOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-alto-grey p-4">
          <div className="flex items-center gap-3">
            <img
              src="/hipo-logo.png"
              alt="HiPo AI Coach"
              className="h-8 w-8 object-contain"
            />
            <span className="font-display text-xl font-bold text-royal-blue">
              HiPo Coach
            </span>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* User Profile Section */}
        <div className="border-b border-alto-grey p-4">
          <div className="flex items-center gap-3">
            <UserButton afterSignOutUrl="/" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-display font-medium text-gray-900 truncate">
                Your Account
              </p>
              <p className="text-xs text-gray-600">Manage your profile</p>
            </div>
          </div>
        </div>

        {/* Enhanced Navigation with Submenus */}
        <nav className="flex-1 overflow-y-auto p-4">
          <div className="space-y-2">
            {navItems.map((item) => {
              const isActive = currentPage === item.id;
              const hasSubmenu = item.submenu && item.submenu.length > 0;
              const isExpanded = expandedMenus.has(item.id);
              
              return (
                <div key={item.id} className="space-y-1">
                  {/* Main Navigation Item */}
                  <div className="flex items-center">
                    {item.action === 'tour' ? (
                      <button
                        onClick={handleQuickTourMobile}
                        className={`flex items-center gap-3 flex-1 rounded-lg px-3 py-3 text-sm font-medium transition-all duration-200 ${isActive
                          ? 'bg-royal-blue text-white shadow-sm'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-royal-blue'
                          }`}
                      >
                        <item.icon className={`h-5 w-5 ${isActive ? '!text-white' : ''}`} strokeWidth={1.5} />
                        <span className={`font-display ${isActive ? '!text-white' : ''}`}>{item.label}</span>
                      </button>
                    ) : item.path ? (
                      <Link
                        href={item.path}
                        onClick={() => handleNavClick(item.path)}
                        className={`flex items-center gap-3 flex-1 rounded-lg px-3 py-3 text-sm font-medium transition-all duration-200 ${isActive
                          ? 'bg-royal-blue text-white shadow-sm'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-royal-blue'
                          }`}
                      >
                        <item.icon className={`h-5 w-5 ${isActive ? '!text-white' : ''}`} strokeWidth={1.5} />
                        <span className={`font-display ${isActive ? '!text-white' : ''}`}>{item.label}</span>
                      </Link>
                    ) : (
                      <div
                        className="flex items-center gap-3 flex-1 rounded-lg px-3 py-3 text-sm font-medium text-gray-700"
                      >
                        <item.icon className="h-5 w-5" strokeWidth={1.5} />
                        <span className="font-display">{item.label}</span>
                      </div>
                    )}
                    {/* Submenu Toggle Button */}
                    {hasSubmenu && (
                      <button
                        onClick={() => toggleSubmenu(item.id)}
                        className="p-2 rounded-lg hover:bg-gray-50 hover:shadow-sm transition-all duration-200 ml-2"
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4 text-royal-blue" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-gray-700" />
                        )}
                      </button>
                    )}
                  </div>
                  
                  {/* Submenu Items */}
                  {hasSubmenu && isExpanded && (
                    <div className="ml-8 space-y-1 border-l border-gray-200 pl-4">
                      {item.submenu.map((subItem) => (
                        <div key={subItem.id}>
                          {subItem.action === 'share' ? (
                            <button
                              onClick={handleRecommendShare}
                              className="flex items-center gap-3 w-full rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:text-royal-blue hover:bg-gray-50 transition-colors"
                            >
                              <subItem.icon className="h-4 w-4" />
                              <span className="font-display">{subItem.label}</span>
                            </button>
                          ) : (
                            <Link
                              href={subItem.path || '#'}
                              onClick={() => handleNavClick(subItem.path || '#')}
                              className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:text-royal-blue hover:bg-gray-50 transition-colors"
                            >
                              <subItem.icon className="h-4 w-4" />
                              <span className="font-display">{subItem.label}</span>
                            </Link>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Sign Out Button */}
          <div className="mt-6 pt-6 border-t border-alto-grey">
            <button
              onClick={handleSignOut}
              className="flex items-center gap-3 w-full rounded-lg px-3 py-3 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogOut className="h-5 w-5" strokeWidth={1.5} />
              <span className="font-display">Sign Out</span>
            </button>
          </div>
        </nav>

        {/* Footer */}
        <div className="border-t border-alto-grey p-4">
          <p className="text-xs text-gray-500 text-center">
            HiPo AI Coach v1.0.0
          </p>
        </div>
      </div>
    </>
  );
};

export default MobileDrawer;
